import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Product, User } from '@spartacus/core';
import { ICON_TYPE } from '@spartacus/storefront';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Observable, Subscription, of, timer, switchMap, catchError, map, finalize, take, tap } from 'rxjs';
import { sellingConfig, renewalConfig, formConfig } from 'src/app/generac/configs/address-details-form.config';
import { GenAsyncValidation } from 'src/app/generac/enums/async-validation.enum';
import { GenInputType } from 'src/app/generac/enums/input-type.enum';
import { GenLoadingEnum } from 'src/app/generac/enums/loading.enum';
import { GenServiceProductType } from 'src/app/generac/enums/service-product-type';
import { GenAddressFormConfig, GenAddressFormConfigItem, GenGenUserRoleValue } from 'src/app/generac/models/address-details.model';
import { GenRenewal, GenRole, GenSubscriptionProduct } from 'src/app/generac/models/subscription.model';
import { SubscriptionsService } from 'src/app/generac/services/subscriptions.service';

@Component({
  selector: 'gen-address-details-form',
  templateUrl: './address-details-form.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class GenAddressDetailsFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions = new Subscription();

  @Input() product: GenSubscriptionProduct;
  @Input() formGroup: FormGroup;
  @Input() checkoutInfo: any;
  @Input() index: number;
  @Output() loaded = new EventEmitter<GenLoadingEnum>();

  renewals: GenRenewal[] | null;
  isSelling: boolean;
  isRenewal: boolean;
  roles: GenRole[] | null;
  formConfig: GenAddressFormConfig[] = [];
  GenInputType = GenInputType;
  iconTypes = ICON_TYPE;
  userAccount$: Observable<User> = this.userAccount.get();
  itemFormGroup: FormGroup = new FormGroup({});

  public get GenServiceProductTypes(): typeof GenServiceProductType {
    return GenServiceProductType;
  }

  constructor(
    protected subscriptionsService: SubscriptionsService,
    private userAccount: UserAccountFacade,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.initConfig();
    this.initForm(this.checkoutInfo);
    this.getRenewalsOrRolesByProductType();
  }

  ngAfterViewInit(): void {
    this.loaded.emit(GenLoadingEnum.Loaded);
  }

  public initForm(checkoutInfo: any) {
    let controlToHighlight: string;

    if (this.formGroup.get(['subscriptions']) && this.itemFormGroup) {
      (this.formGroup.get(['subscriptions']) as FormArray).push(this.itemFormGroup as FormGroup);
    } else {
      this.formGroup.addControl('order', this.itemFormGroup);
    }

    this.formConfig.forEach(field => {
      field.items.forEach(item => {
        const value = this.getFormGroupValue({ ...checkoutInfo, ... this.product }, item);
        const validators = item.validators?.length ? item.validators : [];
        const asyncValidator = this.selectAsyncValidator(item.asyncValidator);

        if (item.isRequired) {
          validators.push(Validators.required);
        }
        this.itemFormGroup.addControl(item.formControlName, new FormControl(value, { validators, asyncValidators: asyncValidator, updateOn: 'blur' }));
      });
    });

    if (controlToHighlight) {
      this.formGroup.get(controlToHighlight).markAsDirty();
    }

    this.cdr.markForCheck();
  }

  onRenewalSelect(value: any) {
    if (value.firstName) {
      this.itemFormGroup.get('firstName').setValue(value.firstName);
    }
    if (value.lastName) {
      this.itemFormGroup.get('lastName').setValue(value.lastName);
    }
    if (value.email) {
      this.itemFormGroup.get('email').setValue(value.email);
    }
    if (value.subscriptionId) {
      this.itemFormGroup.get('subscriptionId').setValue(value.subscriptionId);
    }
    if (value.customerNbr) {
      this.itemFormGroup.get('customerNbr').setValue(value.customerNbr);
    }
    if (value.subEndDate) {
      this.itemFormGroup.get('subEndDate').setValue(value.subEndDate);
    }
  }

  private selectAsyncValidator(asyncValidator: GenAsyncValidation) {
    switch (asyncValidator) {
      case GenAsyncValidation.Email:
        return this.validateSubscriptionEmail();
      default:
        return null;
    }
  }

  private validateOtherEmails(control: AbstractControl, emailsFromSubs: Array<string>) {
    const otherEmailsFromSubs = (this.formGroup.getRawValue().subscriptions as Array<any>)
      .filter((subs: any) => !!subs && subs?.type == GenServiceProductType.SELLING && subs?.email !== control.value)
      .map((subs: any) => subs?.email?.length ? subs?.email : null);
    const formControls = (this.formGroup.controls['subscriptions'] as FormArray).controls as Array<FormGroup<any>>;
    otherEmailsFromSubs.forEach((item) => {
      const indexOfDuplicateInSubs = emailsFromSubs.indexOf(item);
      if (indexOfDuplicateInSubs > -1) {
        formControls?.filter((subs: any) => !!subs && subs?.value.type == GenServiceProductType.SELLING).forEach(
          (formGroup: FormGroup, index) => {
            if (indexOfDuplicateInSubs == index) {
              formGroup.get('email')?.setErrors(null);
            }
          });
      }
    })
  }

  private validateDuplicateEmails(control: AbstractControl) {
    if (this.formGroup.getRawValue().subscriptions?.length > 0) {
      const emailsList = (this.formGroup.getRawValue().subscriptions as Array<any>)
        .filter((subs: any) => !!subs && subs?.type == GenServiceProductType.SELLING)
        .map((subs: any) => subs?.email?.length ? subs?.email : null);
      const matches = emailsList.filter(e => e == control.value);
      let message;
      if (matches.length > 1) {
        const indexOfDuplicateInEmailsList = emailsList?.indexOf(control.value);
        message = indexOfDuplicateInEmailsList > -1 ? { duplicate: true } : null;
      }
      this.validateOtherEmails(control, emailsList);
      control.markAsTouched();
      control.setErrors(null);
      return message;
    }
    return null;
  }

  private validateSubscriptionEmail() {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      const duplicateMessage = this.validateDuplicateEmails(control);
      if (duplicateMessage) {
        return of(duplicateMessage);
      }
      return timer(300)
        .pipe(
          switchMap(() => {
            return this.subscriptionsService.validateEmail(control.value, this.product?.sapMaterialNumber)
              .pipe(catchError(() => of(null)))
          }),
          map((message: string) =>
            message ? message.includes('This email address is already in use') ? { duplicate: message } : { email: true } : null
          ),
          finalize(() => {
            this.itemFormGroup.markAsTouched();
            this.cdr.markForCheck()
          })
        );
    }
  }

  private initConfig(): void {
    switch (this.product?.serviceProductType) {
      case GenServiceProductType.SELLING:
        this.formConfig = sellingConfig as GenAddressFormConfig[];
        this.isSelling = true;
        return;
      case GenServiceProductType.RENEWAL:
      case GenServiceProductType.API_RENEWAL:
        this.formConfig = renewalConfig as GenAddressFormConfig[];
        this.isRenewal = true;
        return;
      default:
        this.formConfig = formConfig as GenAddressFormConfig[];
        return;
    }
  }

  private getFormGroupValue(info: any, item: GenAddressFormConfigItem): string | GenGenUserRoleValue {
    if (info) {
      switch (item?.formControlName) {
        case 'userRole':
          return info[item?.formControlName]?.code;
        case 'materialNumber':
          return this.product?.sapMaterialNumber;
        case 'type':
          return this.product?.serviceProductType;
        default:
          return info[item?.formControlName]
      }
    }
    return item.value;
  }

  private getRenewalsOrRolesByProductType(): void {
    if (this.product?.serviceProductType === GenServiceProductType.RENEWAL || this.product?.serviceProductType === GenServiceProductType.API_RENEWAL) {
      this.getRenewals();
    } else if (this.product?.serviceProductType === GenServiceProductType.SELLING) {
      this.getRoles();
    }
  }

  private getRoles(): void {
    this.subscriptions.add(this.userAccount$
      .pipe(
        take(1),
        switchMap((user: User) => this.subscriptionsService.getUserRoles(user?.uid)),
        tap(roles => {
          this.roles = roles.userRoles;
          this.cdr.detectChanges();
        })
      )
      .subscribe());
  }

  private setPowerPlayApiRenewalFormData(): void {
    this.itemFormGroup.removeControl('firstName');
    this.itemFormGroup.removeControl('lastName');
    this.itemFormGroup.removeControl('email');
    this.itemFormGroup.get('id').setValue(this.product?.subscriptions[0]?.id);
    this.itemFormGroup.get('materialNumber').setValue('POWERPLAYAPIRENEW');
    this.itemFormGroup.get('type').setValue('API_Renewal');
    this.itemFormGroup.updateValueAndValidity();
    this.itemFormGroup.markAsTouched();
  }

  private getRenewals(): void {
    this.subscriptions.add(this.userAccount$
      .pipe(
        take(1),
        switchMap((user: User) => this.subscriptionsService.getCurrentSubscriptions(user?.uid, this.product?.sapMaterialNumber)),
        tap(renewals => {
          if (renewals?.length > 0) {
            this.renewals = renewals;
            if (this.product?.serviceProductType === GenServiceProductType.API_RENEWAL) {
              this.onRenewalSelect(this.renewals[0]);
              this.setPowerPlayApiRenewalFormData();
            }
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
